import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CardHeader,
    Container,
    Grid,
    Stack,
    Typography,
    Link as MuiLink,
    lighten,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";
import { useMemo } from "react";

const GenerateDashboard: React.FC = () => {
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const cards = [
        {
            title: "Generate by Name",
            subtitle: "Already know what to eat? Just tell us the name or description!",
            href: "/generate/name",
            icon: (
                <Box
                    component="img"
                    src="/images/coocoo_gen_name.png"
                    sx={{
                        width: 256,
                        height: 256,
                        objectFit: "contain",
                    }}
                />
            ),

            gradient:
                "linear-gradient(135deg, " +
                lighten(theme.palette.primary.main, 0.75) +
                " 0%, " +
                lighten(theme.palette.primary.main, 0.75) +
                " 45%, " +
                lighten(theme.palette.primary.main, 0.75) +
                " 100%)",
        },
        {
            title: "Generate by Parameters",
            subtitle: "Tell us ingredients, cook time, cuisine, diet, etc — we conjure a recipe.",
            href: "/generate/parameters",
            icon: (
                <Box
                    component="img"
                    src="/images/coocoo_gen_param.png"
                    sx={{
                        width: 256,
                        height: 256,
                        objectFit: "contain",
                    }}
                />
            ),
            gradient:
                "linear-gradient(135deg, " +
                lighten(theme.palette.primary.main, 0.75) +
                " 0%, " +
                lighten(theme.palette.primary.main, 0.75) +
                " 45%, " +
                lighten(theme.palette.primary.main, 0.75) +
                " 100%)",
        },
        {
            title: "Generate from Image",
            subtitle: "Upload a photo of a tasty dish and retrieve a recontructed recipe.",
            href: "/generate/image",
            icon: (
                <Box
                    component="img"
                    src="/images/coocoo_gen_image.png"
                    sx={{
                        width: 256,
                        height: 256,
                        objectFit: "contain",
                    }}
                />
            ),
            gradient:
                "linear-gradient(135deg, " +
                lighten(theme.palette.primary.main, 0.75) +
                " 0%, " +
                lighten(theme.palette.primary.main, 0.75) +
                " 45%, " +
                lighten(theme.palette.primary.main, 0.75) +
                " 100%)",
        },
    ];

    return (
        <Box
            sx={{
                minHeight: "calc(100dvh - 65px)",
                bgcolor: `linear-gradient(180deg, ${theme.palette.grey[50]} 0%, #fff 28%, #fff 100%)`,
                backgroundImage:
                    "radial-gradient(60% 50% at 0% 0%, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0) 100%), radial-gradient(40% 35% at 100% 0%, rgba(255,152,0,0.05) 0%, rgba(255,152,0,0) 100%)",
                pt: 2,
            }}
        >
            <Container sx={{ py: { xs: 3, sm: 5 } }}>
                <Stack spacing={2} sx={{ textAlign: { xs: "left", sm: "center" } }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                        How do you want to cook today?
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ maxWidth: 680, alignSelf: { xs: "flex-start", sm: "center" } }}
                    >
                        Choose a generation method below. Your next tasty creation awaits!
                    </Typography>
                </Stack>

                <Grid container justifyContent="center" spacing={2.5} sx={{ mt: { xs: 2, sm: 4 } }}>
                    {cards.map((c, idx) => (
                        <Grid key={idx}>
                            <Card
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    position: "relative",
                                    width: "400px",
                                    maxWidth: "90vw",
                                    background: `linear-gradient(180deg, rgba(255,255,255,0.80), rgba(255,255,255,0.90)), ${c.gradient}`,
                                    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                                    transition: "transform .18s ease, box-shadow .18s ease",
                                    willChange: "transform",
                                    "&:hover": {
                                        transform: "translateY(-4px)",
                                        boxShadow: "0 18px 50px rgba(0,0,0,0.12)",
                                    },
                                }}
                            >
                                <CardActionArea component={MuiLink} href={c.href} underline="none" sx={{ display: "block" }}>
                                    <Box
                                        sx={{
                                            position: "relative",
                                            pt: "256px",
                                            background: "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.30) 100%)",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 64,
                                                color: theme.palette.text.primary,
                                                backgroundColor: "#FEF8D2",
                                                transform: "translateY(0)",
                                                transition: "transform .2s ease",
                                                "&:hover": { transform: "translateY(-2px)" },
                                            }}
                                        >
                                            {c.icon}
                                        </Box>
                                    </Box>

                                    <CardHeader
                                        title={
                                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                                {c.title}
                                            </Typography>
                                        }
                                        subheader={
                                            <Typography variant="body2" color="text.secondary">
                                                {c.subtitle}
                                            </Typography>
                                        }
                                        sx={{ pb: 0.5 }}
                                    />

                                    <CardContent sx={{ pt: 1.5 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                            <Button
                                                endIcon={<ArrowForwardRoundedIcon />}
                                                size="small"
                                                variant="contained"
                                                sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    px: 1.5,
                                                    "&:active": { transform: "scale(0.98)" },
                                                }}
                                            >
                                                Open
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default GenerateDashboard;
